"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseTokenDetails = exports.ERC721 = exports.MiscToken = exports.ERC20 = exports.BridgedToken = exports.LiquidityPoolToken = exports.LpTokens = exports.Native = exports.decimalsFromJson = exports.addressFromJson = exports.amountFromJson = exports.nameFromJson = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
/**
 * Extracts the token name, removing the 'on Fuse' suffix if present.
 * @param tokenName The original name of the token.
 * @returns The processed token name.
 */
function nameFromJson(tokenName) {
    return tokenName.endsWith('on Fuse') ? tokenName.replace(' on Fuse', '') : tokenName;
}
exports.nameFromJson = nameFromJson;
/**
 * Converts a string value to a bigint, handling null by returning 0.
 * @param value The string value to convert.
 * @returns The bigint representation.
 */
function amountFromJson(value) {
    return value !== null ? BigInt(new bignumber_js_1.default(value).toString()) : BigInt(0);
}
exports.amountFromJson = amountFromJson;
/**
 * Converts a token address to lowercase.
 * @param address The original token address.
 * @returns The lowercase token address.
 */
function addressFromJson(address) {
    return address.toLowerCase();
}
exports.addressFromJson = addressFromJson;
/**
 * Converts a decimal string to a number, defaulting to 0 if null.
 * @param decimals The decimal string to convert.
 * @returns The numeric representation of decimals.
 */
function decimalsFromJson(decimals) {
    return decimals ? parseInt(decimals, 10) : 0;
}
exports.decimalsFromJson = decimalsFromJson;
/**
 * Represents the native token with predefined values and custom amount.
 */
class Native {
    /**
     * Constructs a Native token instance.
     * @param json The JSON object containing the amount.
     */
    constructor(json) {
        this.symbol = 'FUSE';
        this.name = 'Fuse Token';
        this.address = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
        this.decimals = 18;
        this.amount = amountFromJson(json.amount);
    }
}
exports.Native = Native;
/**
 * Represents liquidity pool tokens with dynamic properties.
 */
class LpTokens {
    /**
     * Constructs an LpTokens instance.
     * @param symbol The token symbol.
     * @param name The token name.
     * @param address The token address.
     */
    constructor({ symbol, name, address }) {
        this.symbol = symbol;
        this.name = nameFromJson(name);
        this.address = addressFromJson(address);
    }
    /**
     * Creates an LpTokens instance from a JSON object.
     * @param json The JSON object with token properties.
     * @returns An LpTokens instance.
     */
    static fromJson(json) {
        return new LpTokens({
            symbol: json.symbol,
            name: json.name,
            address: json.address,
        });
    }
    /**
     * Converts the instance to a JSON object.
     * @returns The JSON representation of the instance.
     */
    toJson() {
        return {
            symbol: this.symbol,
            name: this.name,
            address: this.address,
        };
    }
}
exports.LpTokens = LpTokens;
/**
 * Represents a token within a liquidity pool.
 */
class LiquidityPoolToken {
    /**
     * Constructs a LiquidityPoolToken instance.
     * @param json The JSON object containing token and underlying tokens information.
     */
    constructor(json) {
        this.symbol = json.symbol;
        this.name = nameFromJson(json.name);
        this.address = addressFromJson(json.address);
        this.decimals = json.decimals;
        this.underlyingTokens = json.underlyingTokens.map((ut) => LpTokens.fromJson(ut));
    }
}
exports.LiquidityPoolToken = LiquidityPoolToken;
/**
 * Represents a bridged token with additional logo URI.
 */
class BridgedToken {
    /**
     * Constructs a BridgedToken instance.
     * @param json The JSON object containing the token properties.
     */
    constructor(json) {
        this.symbol = json.symbol;
        this.name = nameFromJson(json.name);
        this.address = addressFromJson(json.address);
        this.logoURI = json.logoURI;
        this.decimals = decimalsFromJson(json.decimals);
    }
}
exports.BridgedToken = BridgedToken;
/**
 * Represents an ERC20 token with an amount property.
 */
class ERC20 {
    /**
     * Constructs an ERC20 token instance.
     * @param json The JSON object containing the token properties.
     */
    constructor(json) {
        this.symbol = json.symbol;
        this.name = nameFromJson(json.name);
        this.address = addressFromJson(json.contractAddress || json.address);
        this.decimals = decimalsFromJson(json.decimals);
        this.amount = amountFromJson(json.balance);
    }
}
exports.ERC20 = ERC20;
/**
 * Represents a miscellaneous token with a logo URI.
 */
class MiscToken {
    /**
     * Constructs a MiscToken instance.
     * @param json The JSON object containing the token properties.
     */
    constructor(json) {
        this.symbol = json.symbol;
        this.name = nameFromJson(json.name);
        this.address = addressFromJson(json.address);
        this.logoURI = json.logoURI;
        this.decimals = decimalsFromJson(json.decimals);
    }
}
exports.MiscToken = MiscToken;
/**
 * Represents an ERC721 token, typically without decimals.
 */
class ERC721 {
    /**
     * Constructs an ERC721 token instance.
     * @param json The JSON object containing the token properties.
     */
    constructor(json) {
        this.decimals = 0; // ERC721 tokens typically don't have decimals
        this.symbol = json.symbol;
        this.name = nameFromJson(json.name);
        this.address = addressFromJson(json.contractAddress || json.address);
        this.amount = amountFromJson(json.balance);
    }
}
exports.ERC721 = ERC721;
/**
 * Parses token details from a JSON object and returns the appropriate token instance.
 * @param json The JSON object containing token details.
 * @returns An instance of a token class based on the type specified in the JSON object.
 * @throws Error if the token type is unknown.
 */
function parseTokenDetails(json) {
    switch (json.type) {
        case 'native':
            return new Native(json);
        case 'lp':
            const underlyingTokens = json.underlyingTokens.map(LpTokens.fromJson);
            return new LiquidityPoolToken(Object.assign(Object.assign({}, json), { underlyingTokens }));
        case 'bridged':
            return new BridgedToken(json);
        case 'misc':
            return new MiscToken(json);
        case 'ERC-20':
            return new ERC20(json);
        case 'ERC-721':
            return new ERC721(json);
        default:
            throw new Error('Unknown token type');
    }
}
exports.parseTokenDetails = parseTokenDetails;
